import { TFunction } from "i18next";
import { useAppStore } from "../../../../../hooks/useStores";
import { ColorTypeMappingEnum } from "../../../../../models/common";
import { isAdsEditor } from "../../../../../utils";
import useDesignEditorUtils, { Property } from "../../../../CustomUtils/UseDesignEditor";
import { ICampaignStore } from "../../../../campaign-store";
import Icons from "../../../../components/Icons";
import { Editor } from "../../../../core";
import UseAdsEditorUtils, { logoConfigLabel } from "../../../../hooks/useAdsEditorUtils";
import { ILayer, LayerType } from "../../../../types";
import useSceneOperations from "../Footer/Graphic/Scenes/utils/SceneOperations";
import { ObjectsEnum } from "../Panels/panelItems";

export const ICON_SIZE = 20;

export const OBJECT_TYPES = {
	BACKGROUND: "Background",
	INITIAL_FRAME: "Initial Frame",
	STATIC_IMAGE: "StaticImage",
	STATIC_TEXT: "StaticText",
	STATIC_VECTOR: "StaticVector",
	FRAME: "Frame",
	GROUP: "group",
	STATIC_RECT: "StaticRect",
	STATIC_CIRCLE: "StaticCircle",
	STATIC_TRIANGLE: "StaticTriangle",
};

// TODO To add it when support stroke color

// const getCommonMenus = (t: TFunction) => [
// 	{
// 		onClick: () => {},
// 		icon: <Icons.Palette size={ICON_SIZE} />,
// 		label: t("brandStrokeColor"),
// 		isSubMenu: true,
// 		children: [
// 			{
// 				onClick: () => {},
// 				label: `${t("color")} #1`,
// 			},
// 			{
// 				onClick: () => {},
// 				label: `${t("color")} #2`,
// 			},
// 			{
// 				onClick: () => {},
// 				label: `${t("color")} #3`,
// 			},
// 			{
// 				onClick: () => {},
// 				label: `${t("color")} #4`,
// 			},
// 			{
// 				onClick: () => {},
// 				label: `${t("color")} #5`,
// 			},
// 		],
// 	},
// ];
const getBrandMenus = (
	t: TFunction,
	activeObject: Partial<ILayer>,
	isAdmin: boolean,
	editor: Editor,
	editBrandConfigrationsInAllAds: (
		activeObject: any,
		property: string,
		newValue: string | number | undefined,
	) => void,
	isEditingAllAds: boolean,
) => {
	if (!isAdmin) {
		return [];
	}

	const modifyActiveObjectProperty = (property: Property, value: any, targetObject: any = activeObject) => {
		if (!targetObject[property] || targetObject[property] !== value) {
			targetObject[property] = value;
			editBrandConfigrationsInAllAds(activeObject, property, value);
		} else {
			targetObject[property] = undefined;

			editBrandConfigrationsInAllAds(activeObject, property, undefined);
		}
		editor.canvas.canvas.fire("object:modified", { target: targetObject, invokeSave: !isEditingAllAds });
	};

	const modifyFrameColor = (colorNumber: number) => {
		const frame = editor?.objects.findOneById(ObjectsEnum.InitialFrame);
		if (frame) {
			modifyActiveObjectProperty(Property.COLOR_NUMBER, colorNumber, frame);
		}
	};

	// TODO To add it when support stroke color
	// const commonMenus = getCommonMenus(t);
	const elementName = activeObject?.name ?? activeObject?.type;
	const colorKey = Object.keys(ColorTypeMappingEnum) as Array<keyof typeof ColorTypeMappingEnum>;

	switch (elementName) {
		case OBJECT_TYPES.INITIAL_FRAME || OBJECT_TYPES.BACKGROUND:
			const initialFrame = editor?.objects.findOneById(ObjectsEnum.InitialFrame);
			return [
				{
					onClick: () => {},
					icon: <Icons.Palette size={ICON_SIZE} />,
					label: t("brandFillColor"),
					isSubMenu: true,
					children: [
						{
							onClick: (e: React.MouseEvent<HTMLDivElement>) => {
								modifyFrameColor(1);
								e.stopPropagation();
							},
							label: `${ColorTypeMappingEnum[colorKey[0]]}`,
							Selected: initialFrame?.colorNumber === 1 ? true : false,
						},
						{
							onClick: (e: React.MouseEvent<HTMLDivElement>) => {
								modifyFrameColor(2);
								e.stopPropagation();
							},
							label: `${ColorTypeMappingEnum[colorKey[1]]}`,
							Selected: initialFrame?.colorNumber === 2 ? true : false,
						},
						{
							onClick: (e: React.MouseEvent<HTMLDivElement>) => {
								modifyFrameColor(3);
								e.stopPropagation();
							},
							label: `${ColorTypeMappingEnum[colorKey[2]]}`,
							Selected: initialFrame?.colorNumber === 3 ? true : false,
						},
						{
							onClick: (e: React.MouseEvent<HTMLDivElement>) => {
								modifyFrameColor(4);
								e.stopPropagation();
							},
							label: `${ColorTypeMappingEnum[colorKey[3]]}`,
							Selected: initialFrame?.colorNumber === 4 ? true : false,
						},
						{
							onClick: (e: React.MouseEvent<HTMLDivElement>) => {
								modifyFrameColor(5);
								e.stopPropagation();
							},
							label: `${ColorTypeMappingEnum[colorKey[4]]}`,
							Selected: initialFrame?.colorNumber === 5 ? true : false,
						},
						{
							onClick: (e: React.MouseEvent<HTMLDivElement>) => {
								modifyFrameColor(6);
								e.stopPropagation();
							},
							label: `${ColorTypeMappingEnum[colorKey[5]]}`,
							Selected: initialFrame?.colorNumber === 6 ? true : false,
						},
					],
				},
				{ divider: true },
			];
		default:
			return [];
	}
};

const getBackgroundMenu = (
	t: TFunction,
	activeObject: Partial<ILayer>,
	isAdmin: boolean,
	deleteScene: (id?: string) => Promise<void>,
	duplicateScene: (id?: string) => Promise<void>,
	editor: Editor,
	editBrandConfigrationsInAllAds: (
		activeObject: any,
		property: string,
		newValue: string | number | undefined,
	) => void,
	isEditingAllAds: boolean,
) => [
	{
		onClick: () => {
			duplicateScene(editor?.scene.exportToJSON().id);
		},
		icon: <Icons.Duplicate size={ICON_SIZE} />,
		label: t("duplicate"),
	},
	{
		onClick: () => {
			deleteScene(editor?.scene.exportToJSON().id);
		},
		icon: <Icons.Delete size={ICON_SIZE} />,
		label: t("delete"),
	},
	{ divider: true },

	...getBrandMenus(t, activeObject, isAdmin, editor, editBrandConfigrationsInAllAds, isEditingAllAds),
	{
		onClick: () => {
			editor.objects.lock();
			editor.cancelContextMenuRequest();
		},
		icon: <Icons.Locked size={ICON_SIZE} />,
		label: t("lock"),
	},
];

const getSmartImageMenu = (
	t: TFunction,
	editor: Editor,
	isAdmin: boolean,
	activeObject: Partial<ILayer>,
	editBrandConfigrationsInAllAds: (
		activeObject: any,
		property: string,
		newValue: string | number | undefined,
	) => void,
	getFirstAvailableConfig: (taken: Set<string>, type: LayerType, baseLabel?: string) => string | null,
	getFabricLayerTakenConfigs: (layers: any[], type: LayerType, isLogo?: boolean) => Set<string>,
	campaignStore: ICampaignStore,
	isEditingAllAds: boolean,
) => {
	const originalMenu = getMainMenu(
		t,
		editor,
		isAdmin,
		activeObject,
		editBrandConfigrationsInAllAds,
		getFirstAvailableConfig,
		getFabricLayerTakenConfigs,
		campaignStore,
		isEditingAllAds,
	);

	const excludedLabels = [
		t("copy").toLowerCase(),
		t("paste").toLowerCase(),
		t("duplicate").toLowerCase(),
		t("delete").toLowerCase(),
		t("lock").toLowerCase(),
		t("unlock").toLowerCase(),
	];

	return originalMenu.filter((item) => {
		if ("divider" in item) return false;
		if (!item.label || typeof item.label !== "string") return true;
		const labelKey = item.label.toLowerCase();
		return !excludedLabels.includes(labelKey);
	});
};

const getMainMenu = (
	t: TFunction,
	editor: Editor,
	isAdmin: boolean,
	activeObject: Partial<ILayer>,
	editBrandConfigrationsInAllAds: (
		activeObject: any,
		property: string,
		newValue: string | number | undefined,
	) => void,
	getFirstAvailableConfig: (taken: Set<string>, type: LayerType, baseLabel?: string) => string | null,
	getFabricLayerTakenConfigs: (layers: any[], type: LayerType, isLogo?: boolean) => Set<string>,
	campaignStore: ICampaignStore,
	isEditingAllAds: boolean,
) => [
	{
		onClick: () => {
			editor.objects.copy();
			editor.cancelContextMenuRequest();
		},
		icon: <Icons.Copy size={ICON_SIZE} />,
		label: t("copy"),
	},
	{
		onClick: () => {
			const currentScene = editor?.scene.exportToJSON();
			let usedConfigs: Set<string> | undefined = undefined;
			let firstAvailableConfig = undefined;
			if (currentScene && !isAdsEditor()) {
				usedConfigs = getFabricLayerTakenConfigs(
					currentScene?.layers,
					activeObject?.type as LayerType,
					activeObject?.isLogo,
				);
				firstAvailableConfig = getFirstAvailableConfig(
					usedConfigs,
					activeObject?.type as LayerType,
					activeObject?.isLogo ? logoConfigLabel : "",
				);
				if (!firstAvailableConfig) {
					campaignStore.setProperty("nonAvailableConfigSnackbarOpen", true);
					return;
				}
			}
			editor.objects.paste(firstAvailableConfig);
			editor.cancelContextMenuRequest();
		},
		icon: <Icons.Paste size={ICON_SIZE} />,
		label: t("paste"),
	},
	{
		onClick: async () => {
			const currentScene = editor?.scene.exportToJSON();
			let usedConfigs: Set<string> | undefined = undefined;
			let firstAvailableConfig = undefined;
			if (currentScene && !isAdsEditor()) {
				usedConfigs = getFabricLayerTakenConfigs(
					currentScene?.layers,
					activeObject?.type as LayerType,
					activeObject?.isLogo,
				);
				firstAvailableConfig = getFirstAvailableConfig(
					usedConfigs,
					activeObject?.type as LayerType,
					activeObject?.isLogo ? logoConfigLabel : "",
				);
				if (!firstAvailableConfig) {
					campaignStore.setProperty("nonAvailableConfigSnackbarOpen", true);
					return;
				}
			}
			await editor.objects.copy();
			editor.objects.paste(firstAvailableConfig);
			editor.cancelContextMenuRequest();
		},
		icon: <Icons.Duplicate size={ICON_SIZE} />,
		label: t("duplicate"),
	},
	{
		onClick: () => {
			editor.objects.remove();
			editor.cancelContextMenuRequest();
		},
		icon: <Icons.Delete size={ICON_SIZE} />,
		label: t("delete"),
	},
	{ divider: true },
	{
		onClick: () => {},
		icon: <Icons.Layers size={ICON_SIZE} />,
		label: t("layer"),
		isSubMenu: true,
		children: [
			{
				onClick: () => {
					editor.objects.bringToFront();
					editor.cancelContextMenuRequest();
				},
				label: t("bringToFront"),
			},
			{
				onClick: () => {
					editor.objects.bringForward();
					editor.cancelContextMenuRequest();
				},
				label: t("bringForward"),
			},
			{
				onClick: () => {
					editor.objects.sendBackwards();
					editor.cancelContextMenuRequest();
				},
				label: t("sendBackward"),
			},
			{
				onClick: () => {
					editor.objects.sendToBack();
					editor.cancelContextMenuRequest();
				},
				label: t("sendToBack"),
			},
		],
	},
	{
		onClick: () => {},
		icon: <Icons.Alignment size={ICON_SIZE} />,
		label: t("position"),
		isSubMenu: true,
		children: [
			{
				onClick: () => {
					editor.objects.alignLeft();
				},
				icon: <Icons.AlignLeft size={ICON_SIZE} />,
				label: t("alignLeft"),
			},
			{
				onClick: () => {
					editor.objects.alignCenter();
				},
				icon: <Icons.AlignCenter size={ICON_SIZE} />,
				label: t("alignCenter"),
			},
			{
				onClick: () => {
					editor.objects.alignRight();
				},
				icon: <Icons.AlignRight size={ICON_SIZE} />,
				label: t("alignRight"),
			},
			{
				onClick: () => {
					editor.objects.alignTop();
				},
				icon: <Icons.AlignTop size={ICON_SIZE} />,
				label: t("alignTop"),
			},
			{
				onClick: () => {
					editor.objects.alignMiddle();
				},
				icon: <Icons.AlignMiddle size={ICON_SIZE} />,
				label: t("alignMiddle"),
			},
			{
				onClick: () => {
					editor.objects.alignBottom();
				},
				icon: <Icons.AlignBottom size={ICON_SIZE} />,
				label: t("alignBottom"),
			},
		],
	},
	{ divider: true },
	...getBrandMenus(t, activeObject, isAdmin, editor, editBrandConfigrationsInAllAds, isEditingAllAds),
	{
		onClick: () => {
			// @ts-ignore
			if (activeObject?.lockMovementX) {
				editor.objects.unlock();
				editor.cancelContextMenuRequest();
			} else {
				editor.objects.lock();
				editor.cancelContextMenuRequest();
			}
		},
		// @ts-ignore
		icon: activeObject?.lockMovementX ? <Icons.Unlocked size={ICON_SIZE} /> : <Icons.Locked size={ICON_SIZE} />,
		// @ts-ignore
		label: activeObject?.lockMovementX ? t("unlock") : t("lock"),
	},
];

const getLockedMenu = (t: TFunction, editor: Editor) => [
	{
		onClick: () => {
			editor.objects.unlock();
			editor.cancelContextMenuRequest();
		},
		icon: <Icons.Unlocked size={ICON_SIZE} />,
		label: t("unlock"),
	},
];

const getTextComponentStatusMenu = (t: TFunction) => [
	...Array.from({ length: 5 }, (_, i) => ({
		key: `${t("heading")} #${i + 1}`,
		value: `${t("heading")} #${i + 1}`,
	})).concat(
		...Array.from({ length: 5 }, (_, i) => ({
			key: `${t("body")} #${i + 1}`,
			value: `${t("body")} #${i + 1}`,
		})),
	),
];

const getImageComponentStatusMenu = (t: TFunction) => [
	...Array.from({ length: 5 }, (_, i) => ({
		key: `${t("image")} #${i + 1}`,
		value: `${t("image")} #${i + 1}`,
	})),
];

const getLogoComponentStatusMenu = (t: TFunction) => [
	...Array.from({ length: 5 }, (_, i) => ({
		key: `${t("logo")} #${i + 1}`,
		value: `${t("logo")} #${i + 1}`,
	})),
];

const getShapeComponentStatusMenu = (t: TFunction) => [
	...Array.from({ length: 5 }, (_, i) => ({
		key: `${t("shape")} #${i + 1}`,
		value: `${t("shape")} #${i + 1}`,
	})),
];

const getFabdicShapeComponentStatusMenu = (t: TFunction) => [
	...Array.from({ length: 5 }, (_, i) => ({
		key: `${t("basicShape")} #${i + 1}`,
		value: `${t("basicShape")} #${i + 1}`,
	})),
];

export const ContextMenuData = (t: TFunction, editor: any, isAdmin: boolean, isEditingAllAds: boolean) => {
	const activeObject = editor?.canvas?.canvas.getActiveObject();
	const { deleteScene, duplicateScene } = useSceneOperations();
	const { editBrandConfigrationsInAllAds } = useDesignEditorUtils();
	const { getFabricLayerTakenConfigs, getFirstAvailableConfig } = UseAdsEditorUtils();
	const { campaignStore } = useAppStore();

	return {
		backgroundMenu: getBackgroundMenu(
			t,
			activeObject,
			isAdmin,
			deleteScene,
			duplicateScene,
			editor,
			editBrandConfigrationsInAllAds,
			isEditingAllAds,
		),
		mainMenu: getMainMenu(
			t,
			editor,
			isAdmin,
			activeObject,
			editBrandConfigrationsInAllAds,
			getFirstAvailableConfig,
			getFabricLayerTakenConfigs,
			campaignStore,
			isEditingAllAds,
		),
		smartImageMenu: getSmartImageMenu(
			t,
			editor,
			isAdmin,
			activeObject,
			editBrandConfigrationsInAllAds,
			getFirstAvailableConfig,
			getFabricLayerTakenConfigs,
			campaignStore,
			isEditingAllAds,
		),
		lockedMenu: getLockedMenu(t, editor),
		textComponentStatusMenu: getTextComponentStatusMenu(t),
		imageComponentStatusMenu: getImageComponentStatusMenu(t),
		logoComponentStatusMenu: getLogoComponentStatusMenu(t),
		shapeComponentStatusMenu: getShapeComponentStatusMenu(t),
		basicShapeComponentStatusMenu: getFabdicShapeComponentStatusMenu(t),
	};
};
